/**
 * layout-variants.ts — the variant↔form translation seam (2026-07-28).
 *
 * Variants exist in exactly two places: the OCR classification candidate list
 * and the OCR profile key. Everything downstream — the cross-type gate, the
 * rules engine, export mapping — operates in FORM-ID space and must never see a
 * variant key. These helpers are that boundary.
 *
 * Getting this wrong is silent, not loud: `decideCrossTypeGate` filters rankings
 * against a Set of form ids, so unmapped variant keys match nothing and the
 * F-021 expected-job prior dies without an error. See the pin at
 * apps/worker/src/lib/__tests__/cross-type-gate.variant-pin.test.ts.
 */
/**
 * Mirror of OCR's ProfileFingerprintConfig.SameLayoutFoldThreshold
 * (`/Volumes/StratumDV/Stratum-Dev/platform/Skarn_OCR/src/Skarn.DocumentOcr.Api/Adaptive/ProfileFingerprintConfig.cs`).
 * Capture needs it in two places — picking which variant a manually-classified
 * scan belongs to (Task 9), and deciding whether to show the "New layout of
 * this form" checkbox (Task 11). Keep the two in sync; there is a drift test
 * for it in `__tests__/layout-variants.test.ts`.
 */
export const SAME_LAYOUT_FOLD_THRESHOLD = 0.55;
/** Parent form id for an OCR profile key, or null when the key is unknown. */
export function resolveVariantToForm(ocrProfileKey, variants) {
    return variants.find((v) => v.ocrProfileKey === ocrProfileKey)?.jobTypeFormId ?? null;
}
/**
 * Rewrite rankings from variant-key space into form-id space, keeping the
 * best-scoring variant per form. Unknown keys are dropped rather than passed
 * through: a key we cannot attribute to a form must not reach the gate.
 */
export function collapseRankingsToForms(rankings, variants) {
    const best = new Map();
    for (const ranking of rankings) {
        const formId = resolveVariantToForm(ranking.profileId, variants);
        if (formId === null)
            continue;
        const current = best.get(formId);
        if (current === undefined || ranking.score > current.score) {
            best.set(formId, { ...ranking, profileId: formId });
        }
    }
    return [...best.values()];
}
//# sourceMappingURL=layout-variants.js.map